import React from 'react';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import { Typography, utils } from '@deeptrue-frontend/ui';
import { useFormatMessage } from 'components/IntlProvider/hooks/hooks';
import ContentBox from './ContentBox';
import BarChart from './BarChart';
import { SampleStructureProps } from '../../types';

const Container = styled.div`
  background-color: #fff;
  border: 1px solid #e3e5e8;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
`;

const Dot = styled.span`
  display: block;
  width: 10px;
  height: 10px;
  background-color: #1656e9;
  margin-right: 0.5rem;
  border-radius: 50%;
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const Collectable = styled.div`
  display: flex;
  align-items: flex-end;
`;

const CollectedSurveys = styled(Typography)`
  font-weight: 500;
  font-size: 1.5rem;
  line-height: 1.25;
  margin-left: 0.5rem;
`;

const Subtitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
`;

interface Props {
  data: SampleStructureProps;
}

const SampleStructure = ({ data }: Props) => {
  const t = useFormatMessage();
  const { locale } = useIntl();
  const { i18n } = utils;
  const { collectedSurveys, gender, age, countries, languages, users } = data;

  const areCountriesProvided = countries && countries.length > 0;
  const areLanguagesProvided = languages && languages.length > 0;

  return (
    <Container>
      <Title>
        <Typography fontWeight="500" fontSize="1.25rem">
          {t('reports.sampleStructure.title', 'Survey Sample Structure')}
        </Typography>
        <Collectable>
          <Typography fontWeight="500" fontSize="1rem">
            {t('reports.sampleStructure.collectedSurvey', 'Collected Surveys')}
          </Typography>
          <CollectedSurveys>{collectedSurveys || 0}</CollectedSurveys>
        </Collectable>
      </Title>
      <Subtitle>
        <Dot />
        <Typography fontWeight="500" fontSize="0.875rem">
          {t(
            'reports.sampleStructure.subtitle',
            'Minimum of 50 to be evaluable reached.'
          )}
        </Typography>
      </Subtitle>
      <Content>
        <ContentBox title={t('reports.sampleStructure.age', 'Age')}>
          <BarChart
            label="15-29y"
            value={age['15-29']}
            isEvaluableReached={(collectedSurveys * age['15-29']) / 100 > 49}
          />
          <BarChart
            label="30-49y"
            value={age['30-49']}
            isEvaluableReached={(collectedSurveys * age['30-49']) / 100 > 49}
          />
          <BarChart
            label="50-65y"
            value={age['50-65']}
            isEvaluableReached={(collectedSurveys * age['50-65']) / 100 > 49}
          />
          <BarChart
            label="66y+"
            value={age['66+']}
            isEvaluableReached={(collectedSurveys * age['66+']) / 100 > 49}
          />
        </ContentBox>
        <ContentBox title={t('reports.sampleStructure.gender', 'Gender')}>
          <BarChart
            label={t('reports.sampleStructure.female', 'Woman')}
            value={gender.female}
            isEvaluableReached={(collectedSurveys * gender.female) / 100 > 49}
          />
          <BarChart
            label={t('reports.sampleStructure.male', 'Man')}
            value={gender.male}
            isEvaluableReached={(collectedSurveys * gender.male) / 100 > 49}
          />
          <BarChart
            label={t('reports.sampleStructure.other', 'Other')}
            value={gender.other}
            isEvaluableReached={(collectedSurveys * gender.other) / 100 > 49}
          />
        </ContentBox>
        {users && (
          <ContentBox title={t('reports.sampleStructure.users', 'Users')}>
            <BarChart
              label={t('reports.sampleStructure.users', 'Users')}
              value={users.user}
              isEvaluableReached={(collectedSurveys * users.user) / 100 > 49}
            />
            <BarChart
              label={t('reports.sampleStructure.nonUsers', 'Non-users')}
              value={users.non_user}
              isEvaluableReached={
                (collectedSurveys * users.non_user) / 100 > 49
              }
            />
            <BarChart
              label={t('reports.sampleStructure.dontKnow', `Don't know`)}
              value={users.dont_know}
              isEvaluableReached={
                (collectedSurveys * users.dont_know) / 100 > 49
              }
            />
          </ContentBox>
        )}
        {areLanguagesProvided && (
          <ContentBox title={t('reports.sampleStructure.language', 'Language')}>
            {languages.map(({ value, label }) => (
              <BarChart
                key={label}
                label={i18n.translateLanguage(label, locale)}
                value={value}
                isEvaluableReached={(collectedSurveys * value) / 100 > 49}
              />
            ))}
          </ContentBox>
        )}
        {areCountriesProvided && (
          <ContentBox title={t('reports.sampleStructure.country', 'Country')}>
            {countries.map(({ value, label }) => (
              <BarChart
                key={label}
                label={i18n.translateCountry(label, locale)}
                value={value}
                isEvaluableReached={(collectedSurveys * value) / 100 > 49}
              />
            ))}
          </ContentBox>
        )}
      </Content>
    </Container>
  );
};

export default SampleStructure;
